import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { getFeatureFlags } from "../../API/async";

export interface FeatureFlags {
  splitPaymentsEnabled: boolean;
  achPaymentMethodEnabled: boolean;
  sellerWalletEnabled?: boolean;
}

interface FeatureFlagsContextValue {
  featureFlags: FeatureFlags;
  isLoading: boolean;
  setFeatureFlags: (flags: FeatureFlags) => void;
}

enum FeatureFlagKeys {
  SplitPayments = "split_payments_shiplist",
  AchPaymentMethod = "ach_payment_method",
  SellerWallet = "seller_wallet_cash_out",
}

const defaultFeatureFlags: FeatureFlagsContextValue = {
  featureFlags: {
    splitPaymentsEnabled: false,
    achPaymentMethodEnabled: false,
    sellerWalletEnabled: false,
  },
  isLoading: true,
  setFeatureFlags: () => {},
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(
  defaultFeatureFlags
);
export const getFeatureFlagsContext = (): FeatureFlagsContextValue =>
  useContext(FeatureFlagsContext);

export const FeatureFlagsProvider: React.FC = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    defaultFeatureFlags.featureFlags
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const featureFlagKeys = Object.values(FeatureFlagKeys);
        const featureFlagResults = await getFeatureFlags(featureFlagKeys);

        const mappedFeatureFlags: FeatureFlags = {
          splitPaymentsEnabled:
            featureFlagResults[FeatureFlagKeys.SplitPayments] ?? false,
          achPaymentMethodEnabled:
            featureFlagResults[FeatureFlagKeys.AchPaymentMethod] ?? false,
          sellerWalletEnabled:
            featureFlagResults[FeatureFlagKeys.SellerWallet] ?? false,
        };

        setFeatureFlags(mappedFeatureFlags);
      } finally {
        setIsLoading(false);
      }
    };

    loadFeatureFlags();
  }, []);

  const value = useMemo(() => ({ featureFlags, isLoading, setFeatureFlags }), [
    featureFlags,
    isLoading,
  ]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
